/**
 * PurchaseHistoryPage Component
 *
 * Page dédiée à l'historique des achats de l'utilisateur.
 * Affiche tous les achats passés avec détails et statistiques.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../store';

/**
 * Purchase history entry from API
 */
interface PurchaseHistoryEntry {
  id: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  paymentType: 'subscription' | 'one-time' | null;
  amountPaid: number;
  status: 'active' | 'completed' | 'cancelled' | 'expired';
  endedReason: string | null;
  createdAt: string;
}

/**
 * Purchase history stats from API
 */
interface PurchaseStats {
  totalSubscriptions: number;
  totalSpent: number;
  firstSubscriptionDate: string | null;
  lastSubscriptionDate: string | null;
}

/**
 * Format date with time
 */
function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format short date
 */
function formatShortDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function PurchaseHistoryPage() {
  const { token, isAuthenticated } = useStore();

  const [history, setHistory] = useState<PurchaseHistoryEntry[]>([]);
  const [stats, setStats] = useState<PurchaseStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch purchase history from API
   */
  const fetchHistory = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/subscription/history', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération de l\'historique');
      }

      const data = await response.json();
      if (data.success) {
        setHistory(data.data || []);
        setStats(data.stats || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchHistory();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, fetchHistory]);

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-4">
          Historique des achats
        </h1>
        <p className="text-slate-600 mb-6">
          Connectez-vous pour voir votre historique d'achats.
        </p>
        <Link to="/login" className="btn-primary inline-block">
          Se connecter
        </Link>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-4 gap-4">
            <div className="h-24 bg-slate-200 rounded-xl"></div>
            <div className="h-24 bg-slate-200 rounded-xl"></div>
            <div className="h-24 bg-slate-200 rounded-xl"></div>
            <div className="h-24 bg-slate-200 rounded-xl"></div>
          </div>
          <div className="h-64 bg-slate-200 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  // Get status badge
  const getStatusBadge = (status: string, endedReason: string | null) => {
    switch (status) {
      case 'active':
        return { style: 'bg-green-100 text-green-700', label: '✓ Actif' };
      case 'expired':
        return { style: 'bg-slate-100 text-slate-600', label: '⏱ Expiré' };
      case 'cancelled':
        const reason = endedReason === 'cancelled_by_admin' ? ' (admin)' : '';
        return { style: 'bg-orange-100 text-orange-700', label: `✕ Annulé${reason}` };
      case 'completed':
        return { style: 'bg-blue-100 text-blue-700', label: '✓ Terminé' };
      default:
        return { style: 'bg-slate-100 text-slate-500', label: status };
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/subscription"
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <svg className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </Link>
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">
            Historique des achats
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Consultez tous vos achats et abonnements passés
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          <p>{error}</p>
        </div>
      )}

      {/* Stats Cards */}
      {stats && stats.totalSubscriptions > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-4 text-white">
            <p className="text-xs text-primary-100 uppercase tracking-wider font-medium">Total dépensé</p>
            <p className="text-2xl font-bold mt-1">{stats.totalSpent.toFixed(2)} €</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Achats</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stats.totalSubscriptions}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Premier achat</p>
            <p className="text-sm font-semibold text-slate-700 mt-1">{formatShortDate(stats.firstSubscriptionDate)}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">Dernier achat</p>
            <p className="text-sm font-semibold text-slate-700 mt-1">{formatShortDate(stats.lastSubscriptionDate)}</p>
          </div>
        </div>
      )}

      {/* History List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <h2 className="font-semibold text-lg text-slate-900">
            Détail des achats
          </h2>
        </div>

        {history.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <svg className="w-16 h-16 mx-auto mb-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium mb-2">Aucun achat</p>
            <p className="text-sm">Vous n'avez pas encore effectué d'achat.</p>
            <Link
              to="/subscription"
              className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Découvrir Premium
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Période
                  </th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((entry) => {
                  const badge = getStatusBadge(entry.status, entry.endedReason);
                  return (
                    <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-4 px-6">
                        <div className="text-sm text-slate-900">{formatDateTime(entry.createdAt)}</div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {entry.paymentType === 'subscription' ? '🔄' : '📅'}
                          </span>
                          <span className="text-sm text-slate-700">
                            {entry.paymentType === 'subscription' ? 'Abonnement annuel' : 'Achat mensuel'}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-sm text-slate-900">
                          {formatShortDate(entry.subscriptionStartDate)}
                        </div>
                        <div className="text-xs text-slate-500">
                          → {formatShortDate(entry.subscriptionEndDate)}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-sm font-semibold text-slate-900">
                          {entry.amountPaid.toFixed(2)} €
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${badge.style}`}>
                          {badge.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Back link */}
      <div className="mt-8 text-center">
        <Link
          to="/subscription"
          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
        >
          ← Retour à mon abonnement
        </Link>
      </div>
    </div>
  );
}

export default PurchaseHistoryPage;
